import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { History, Inbox, LayoutGrid, Loader2, StickyNote } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { cn } from '@/utils/cn'
import type { Bloco, BlocoDetail, BlocoLabel } from '@/types/bloco'
import { BlocoCard } from './BlocoCard'
import { BlocoComposer } from './BlocoComposer'
import { BlocoThreadModal } from './BlocoThreadModal'
import { groupActiveBlocos, groupHistoryBlocos, type BlocosTab } from './blocosUtils'

interface BlocosModalProps {
  open: boolean
  onClose: () => void
  items: Bloco[]
  loading: boolean
  saving: boolean
  currentUserId?: string
  threadDetail: BlocoDetail | null
  threadLoading: boolean
  threadSaving: boolean
  labels?: BlocoLabel[]
  labelsLoading?: boolean
  onCreateLabel?: (name: string, color: string) => Promise<unknown>
  onLoad: () => void
  onOpenThread: (id: string) => Promise<unknown>
  onCloseThread: () => void
  onAdd: (
    payload: {
      title: string
      subject: string
      body?: string
      type: 'note' | 'return'
      dueAt?: string | null
      dueTime?: string | null
      memberUserIds?: string[]
      labelIds?: string[]
    },
    attachments?: File[],
  ) => Promise<unknown>
  onComplete: (id: string) => void
  onDelete: (id: string) => void
  onSendThreadMessage: (id: string, body: string) => Promise<unknown>
  onSaveThreadMembers: (id: string, memberUserIds: string[]) => Promise<unknown>
  onUploadThreadAttachment: (id: string, file: File) => Promise<unknown>
  onAssignLabels?: (id: string, labelIds: string[]) => void
}

function BlocosListTabs({
  tab,
  activeCount,
  historyCount,
  onChange,
}: {
  tab: BlocosTab
  activeCount: number
  historyCount: number
  onChange: (tab: BlocosTab) => void
}) {
  return (
    <div className='mb-3 flex gap-1 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] p-1'>
      <button
        type='button'
        onClick={() => onChange('active')}
        className={cn(
          'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors',
          tab === 'active'
            ? 'bg-bg-card text-text-primary shadow-sm'
            : 'text-text-muted hover:text-text-primary',
        )}
      >
        <Inbox size={14} />
        Ativos
        {activeCount > 0 && <span className='text-[10px] opacity-70'>({activeCount})</span>}
      </button>
      <button
        type='button'
        onClick={() => onChange('history')}
        className={cn(
          'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors',
          tab === 'history'
            ? 'bg-bg-card text-text-primary shadow-sm'
            : 'text-text-muted hover:text-text-primary',
        )}
      >
        <History size={14} />
        Histórico
        {historyCount > 0 && <span className='text-[10px] opacity-70'>({historyCount})</span>}
      </button>
    </div>
  )
}

export function BlocosModal({
  open,
  onClose,
  items,
  loading,
  saving,
  currentUserId,
  threadDetail,
  threadLoading,
  threadSaving,
  onLoad,
  onOpenThread,
  onCloseThread,
  onAdd,
  onComplete,
  onDelete,
  onSendThreadMessage,
  onSaveThreadMembers,
  onUploadThreadAttachment,
  labels = [],
  labelsLoading: _labelsLoading = false,
  onCreateLabel,
  onAssignLabels,
}: BlocosModalProps) {
  const [threadOpen, setThreadOpen] = useState(false)
  const [tab, setTab] = useState<BlocosTab>('active')
  const onLoadRef = useRef(onLoad)
  onLoadRef.current = onLoad

  useEffect(() => {
    if (open) onLoadRef.current()
  }, [open])

  useEffect(() => {
    if (!open) {
      setThreadOpen(false)
      setTab('active')
      onCloseThread()
    }
  }, [open, onCloseThread])

  const activeSections = useMemo(() => groupActiveBlocos(items, currentUserId), [items, currentUserId])
  const historySections = useMemo(() => groupHistoryBlocos(items), [items])
  const sections = tab === 'active' ? activeSections : historySections

  const activeCount = items.filter((b) => b.status === 'open').length
  const historyCount = items.filter((b) => b.status === 'done').length
  const openCount = activeCount

  const handleOpenThread = useCallback(
    async (id: string) => {
      setThreadOpen(true)
      await onOpenThread(id)
    },
    [onOpenThread],
  )

  const handleCloseThread = useCallback(() => {
    setThreadOpen(false)
    onCloseThread()
  }, [onCloseThread])

  const handleComplete = useCallback(
    (id: string) => {
      onComplete(id)
      setTab('history')
    },
    [onComplete],
  )

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title='Blocos'
        description={
          openCount > 0
            ? `${openCount} ${openCount === 1 ? 'anotação aberta' : 'anotações abertas'}`
            : 'Anotações compartilhadas com sua equipe'
        }
        size='3xl'
        className='max-h-[min(860px,94vh)]'
      >
        <div className='mb-3 flex justify-end'>
          <Link
            to='/tarefas'
            onClick={onClose}
            className='inline-flex items-center gap-1.5 text-xs font-medium text-[var(--brand-primary)] hover:underline'
          >
            <LayoutGrid size={14} />
            Ver quadro
          </Link>
        </div>

        <div className='grid gap-5 lg:grid-cols-[minmax(340px,420px)_1fr] lg:items-start'>
          <BlocoComposer saving={saving} currentUserId={currentUserId} onSubmit={onAdd} />

          <div className='min-h-[320px] rounded-xl border border-[var(--glass-border)] bg-bg-card/40 p-3 lg:min-h-[520px]'>
            <BlocosListTabs
              tab={tab}
              activeCount={activeCount}
              historyCount={historyCount}
              onChange={setTab}
            />

            {loading ? (
              <div className='flex h-full min-h-[240px] flex-col items-center justify-center gap-2 text-text-muted'>
                <Loader2 size={24} className='animate-spin' />
                <p className='text-sm'>Carregando blocos…</p>
              </div>
            ) : sections.length === 0 ? (
              <div className='flex h-full min-h-[240px] flex-col items-center justify-center gap-3 text-center'>
                <div className='flex h-12 w-12 items-center justify-center rounded-full bg-[var(--glass-bg)]'>
                  <StickyNote size={22} className='text-text-muted' strokeWidth={1.5} />
                </div>
                <p className='text-sm font-medium text-text-primary'>
                  {tab === 'history' ? 'Nenhum bloco no histórico' : 'Nenhum bloco ativo'}
                </p>
                <p className='max-w-sm text-xs text-text-muted'>
                  {tab === 'history'
                    ? 'Blocos concluídos aparecem aqui para consulta.'
                    : 'Crie blocos para lembretes e follow-ups. Ao concluir, vão para Histórico.'}
                </p>
              </div>
            ) : (
              <div className='max-h-[min(580px,68vh)] space-y-4 overflow-y-auto pr-1'>
                {sections.map((section) => (
                  <section key={section.key} className='space-y-2'>
                    {tab === 'active' && (
                      <h3 className='text-xs font-semibold uppercase tracking-wide text-text-muted'>
                        {section.title}
                      </h3>
                    )}
                    <div className='space-y-2'>
                      {section.items.map((bloco) => (
                        <BlocoCard
                          key={bloco.id}
                          bloco={bloco}
                          sectionKey={section.key}
                          isOwner={bloco.userId === currentUserId}
                          currentUserId={currentUserId}
                          onOpen={(id) => void handleOpenThread(id)}
                          onComplete={handleComplete}
                          onDelete={onDelete}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <BlocoThreadModal
        open={threadOpen}
        detail={threadDetail}
        loading={threadLoading}
        saving={threadSaving}
        currentUserId={currentUserId}
        onClose={handleCloseThread}
        onComplete={handleComplete}
        onDelete={onDelete}
        onSendMessage={onSendThreadMessage}
        onSaveMembers={onSaveThreadMembers}
        onUploadAttachment={onUploadThreadAttachment}
        labels={labels}
        onAssignLabels={onAssignLabels}
        onCreateLabel={onCreateLabel}
      />
    </>
  )
}
